import PropTypes from 'prop-types';
import {
  useMemo, useState, useEffect, useCallback, createContext, useContext,
} from 'react';
// @mui
import { CssBaseline } from '@mui/material';
import { ThemeProvider as MUIThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
//
import { criarPalette } from './palette';
import shadows from './shadows';
import typography from './typography';
import GlobalStyles from './globalStyles';
import customShadows from './customShadows';
import componentsOverride from './overrides';
import { aplicarPaleta, derivarTokens, PALETA_PADRAO } from '../styles/styleUtils';

// ----------------------------------------------------------------------
// Contexto do tema — permite que a tela de Configuração da Empresa troque
// a paleta em tempo de execução (inclusive para pré-visualizar antes de
// salvar).
//
//   const { paleta, trocarPaleta, versao } = useTema();
//   trocarPaleta({ primaria: '#1565C0', escura: '#1A1A2E', ... });
//
// `versao` é um contador que muda a cada troca. Use como `key` em gráficos
// (Recharts/ApexCharts leem a cor no momento em que montam, então precisam
// ser remontados para pegar a paleta nova):
//
//   <ResponsiveContainer key={versao}> ... </ResponsiveContainer>
// ----------------------------------------------------------------------

const TemaContext = createContext({
  paleta: null,
  trocarPaleta: () => {},
  versao: 0,
});

export const useTema = () => useContext(TemaContext);

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export default function ThemeProvider({ children }) {
  // paleta cadastrada pela empresa; null = padrão TriAxis
  const [paleta, setPaleta] = useState(null);
  // tokens derivados vigentes — é deles que o tema do MUI é montado
  const [tokens, setTokens] = useState(() => derivarTokens(PALETA_PADRAO));
  // contador de trocas — serve de `key` para remontar gráficos
  const [versao, setVersao] = useState(0);

  const trocarPaleta = useCallback((nova) => {
    // aplicarPaleta grava as variáveis CSS e devolve os tokens derivados
    setTokens(aplicarPaleta(nova || PALETA_PADRAO));
    setPaleta(nova || null);
    setVersao((v) => v + 1);
  }, []);

  // Busca a paleta da empresa no boot. Import dinâmico para não acoplar o
  // tema ao módulo de api no carregamento (e evitar ciclo de importação).
  useEffect(() => {
    let cancelado = false;
    import('../pages/api')
      .then(({ getEmpresaAtual }) => getEmpresaAtual())
      .then((empresa) => {
        if (cancelado || !empresa?.paleta) return;
        trocarPaleta(empresa.paleta);
      })
      .catch(() => {
        // Sem sessão (tela de login) ou sem empresa cadastrada:
        // mantém os padrões TriAxis já aplicados pelo styleUtils.
      });
    return () => {
      cancelado = true;
    };
  }, [trocarPaleta]);

  // O tema é recriado quando os tokens mudam — dependência real do useMemo,
  // e não um contador artificial.
  const theme = useMemo(() => {
    const novoTema = createTheme({
      palette: criarPalette(tokens),
      shape: { borderRadius: 6 },
      typography,
      shadows: shadows(),
      customShadows: customShadows(),
    });
    novoTema.components = componentsOverride(novoTema);
    return novoTema;
  }, [tokens]);

  const valorContexto = useMemo(
    () => ({ paleta, trocarPaleta, versao }),
    [paleta, trocarPaleta, versao]
  );

  return (
    <TemaContext.Provider value={valorContexto}>
      <StyledEngineProvider injectFirst>
        <MUIThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyles />
          {children}
        </MUIThemeProvider>
      </StyledEngineProvider>
    </TemaContext.Provider>
  );
}